/**
 * "Turn on notifications so you don't miss an interview" (Derek Knost, 2026-10-14: "heavily recommend
 * that people allow notifications"). Shown on the dashboards and right after someone sends or receives
 * an interview request. The browser's permission popup opens only from the "Turn on" click, never on
 * page load. "Not now" hides it for 7 days on this device.
 *
 * iPhone and iPad in a browser tab: push only works once Arevina is on the Home Screen (Apple), so the
 * banner shows Apple's steps instead of a button that cannot work.
 */
import { useEffect, useState } from 'react';
import { Bell, Loader2, Share } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuthCtx } from '../../context/AuthContext';
import { isPushOnHere, isPushPromptSnoozed, permissionState, pushSupport, snoozePushPrompt, turnOnPush } from '../../lib/notifications/push';

export const PUSH_OFF_HERE_KEY = 'rr-push-off-here';

function turnedOffHere(): boolean {
  try {
    return localStorage.getItem(PUSH_OFF_HERE_KEY) === '1';
  } catch {
    return false;
  }
}

export default function PushPromptBanner({ context = 'dashboard' }: { role?: 'emt' | 'employer'; context?: 'dashboard' | 'interview' }) {
  const { user } = useAuthCtx();
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState<'ask' | 'ios'>('ask');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (!user || isPushPromptSnoozed() || turnedOffHere()) return;
    const support = pushSupport();
    if (support.kind === 'ios_needs_home_screen') {
      setMode('ios');
      setShow(true);
      return;
    }
    if (support.kind !== 'supported') return;
    const perm = permissionState();
    if (perm === 'denied') return;
    if (perm === 'granted') {
      isPushOnHere().then((on) => alive && setShow(!on)).catch(() => undefined);
      return;
    }
    setShow(true);
    return () => {
      alive = false;
    };
  }, [user]);

  if (!show) return null;

  const notNow = () => {
    snoozePushPrompt();
    setShow(false);
  };
  const turnOn = async () => {
    setBusy(true);
    setResult(null);
    const r = await turnOnPush();
    setBusy(false);
    if (r === 'on') {
      setResult('Notifications are on for this device.');
      window.setTimeout(() => setShow(false), 2500);
    } else if (r === 'denied') setResult('Notifications are blocked for Arevina in this browser. You can allow them in the browser\'s site settings.');
    else if (r === 'dismissed') setResult(null);
    else setResult('Could not turn on notifications on this device. Please try again.');
  };

  const heading = context === 'interview' ? "Turn on notifications so you know when they answer" : "Turn on notifications so you don't miss an interview";

  return (
    <div className="mb-4 rounded-2xl border border-[var(--rr-red)]/30 bg-[var(--rr-red-light)] p-4" data-testid="push-prompt" data-mode={mode} data-context={context}>
      <div className="flex items-start gap-3">
        <Bell className="mt-0.5 h-5 w-5 shrink-0 text-[var(--text-brand)]" aria-hidden />
        <div className="min-w-0 flex-1">
          {mode === 'ios' ? (
            <>
              <p className="font-semibold text-[var(--text-primary)]">Add Arevina to your Home Screen to get notifications</p>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">On iPhone and iPad, notifications work once Arevina is on your Home Screen.</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-[var(--text-primary)]" data-testid="push-ios-steps">
                <li>
                  Tap the Share button <Share className="inline h-4 w-4 align-text-bottom" aria-label="Share" /> (in Safari, it may be under the More button first).
                </li>
                <li>Scroll down and tap Add to Home Screen.</li>
                <li>Turn on Open as Web App, then tap Add.</li>
                <li>Open Arevina from your Home Screen, sign in and tap Turn on.</li>
              </ol>
            </>
          ) : (
            <>
              <p className="font-semibold text-[var(--text-primary)]">{heading}</p>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">Get interview requests, answers and new messages on this device, even when Arevina is closed.</p>
            </>
          )}
          {result && <p className="mt-2 text-sm font-medium text-[var(--text-primary)]" role="status" data-testid="push-prompt-result">{result}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {mode === 'ask' && (
              <Button className="min-h-[44px] bg-[var(--rr-red)] text-white" disabled={busy} onClick={() => void turnOn()} data-testid="push-turn-on">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Turn on'}
              </Button>
            )}
            <Button variant="outline" className="min-h-[44px]" disabled={busy} onClick={notNow} data-testid="push-not-now">
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
